import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Droplet, Snowflake, Zap, Flame, Skull, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const RACES = [
  {
    id: 'humans', code: '01', name: 'HUMANS', formula: 'O₂', element: 'OXYGEN',
    elementIcon: Droplet, tint: '#FF8618', rgb: '255,134,24',
    desc: 'Adaptable explorers and balanced survivors. Masters of every environment — the versatile backbone of the galactic war.',
    image: '/races/humans.jpg',
  },
  {
    id: 'aquarions', code: '02', name: 'AQUARIONS', formula: 'CH₄', element: 'METHANE',
    elementIcon: Snowflake, tint: '#43CFFF', rgb: '67,207,255',
    desc: 'Aquatic ice-world species born in frozen methane seas. Silent, fluid and lethal beneath the surface.',
    image: '/races/aquarions.jpg',
  },
  {
    id: 'chronobots', code: '03', name: 'CHRONOBOTS', formula: 'H₂', element: 'HYDROGEN',
    elementIcon: Zap, tint: '#39E0C3', rgb: '57,224,195',
    desc: 'Hydrogen-reactor war machines. Relentless, precise and immune to fear — time itself bends to their assault.',
    image: '/races/chronobots.jpg',
  },
  {
    id: 'cosmorians', code: '04', name: 'COSMORIANS', formula: 'SO₂', element: 'SULFUR GASES',
    elementIcon: Flame, tint: '#FF7A1A', rgb: '255,122,26',
    desc: 'Hulking giants forged in sulfur-storm worlds. Brute force incarnate — they crush all who oppose them.',
    image: '/races/cosmorians.jpg',
  },
  {
    id: 'genomorphs', code: '05', name: 'GENOMORPHS', formula: 'Cl₂', element: 'CHLORINE',
    elementIcon: Skull, tint: '#72F052', rgb: '114,240,82',
    desc: 'Engineered demons shaped by chlorine mutation. Fast, feral and endlessly adaptive predators.',
    image: '/races/genomorphs.jpg',
  },
];

// triangular / arrowhead stagger offsets (px) for the left card stack
const OFFSETS = [0, 34, 62, 30, 0];

const CursorSvg = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 2 L4 19 L8.6 14.6 L11.8 21.4 L14.6 20.1 L11.4 13.4 L18 13.4 Z"
      fill="#ffffff"
      stroke="rgba(80,180,255,0.95)"
      strokeWidth="0.7"
      strokeLinejoin="round"
    />
  </svg>
);

function buildPath(pts) {
  if (pts.length < 2) return '';
  const d = [`M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d.push(`C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`);
  }
  return d.join(' ');
}

const PARTICLES = Array.from({ length: 16 }).map((_, i) => ({
  left: `${(i * 61) % 100}%`,
  top: `${20 + ((i * 37) % 70)}%`,
  size: 2 + (i % 3),
  dur: 6 + (i % 5),
  delay: (i % 6) * 0.9,
}));

const Races = () => {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);
  const timeoutsRef = useRef([]);

  const [inView, setInView] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [points, setPoints] = useState([]);
  const [cursor, setCursor] = useState({ x: 0, y: 0, dur: 800 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [manual, setManual] = useState(false);
  const [pulsing, setPulsing] = useState(false);

  const active = RACES[activeIndex];
  const activeRef = useRef(activeIndex);
  activeRef.current = activeIndex;
  const wheelLock = useRef(false);

  const measure = useCallback(() => {
    const c = containerRef.current;
    if (!c) return;
    const cr = c.getBoundingClientRect();
    const pts = cardRefs.current.map((el) => {
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left - cr.left + r.width * 0.5, y: r.top - cr.top + r.height * 0.5 };
    }).filter(Boolean);
    if (pts.length) setPoints(pts);
  }, []);

  // trigger animations when scrolled into view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setInView(true); }),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const t = setTimeout(measure, 120);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [measure, inView]);

  // pulse button + ring on race change
  useEffect(() => {
    setPulsing(true);
    const t = setTimeout(() => setPulsing(false), 620);
    return () => clearTimeout(t);
  }, [activeIndex]);

  const clearTimers = () => { timeoutsRef.current.forEach(clearTimeout); timeoutsRef.current = []; };

  // auto cursor demo
  useEffect(() => {
    if (!inView || manual || points.length < 5) return;
    // reach card i -> { move duration, pause after }
    const steps = [
      { i: 0, move: 700, pause: 600 },
      { i: 1, move: 800, pause: 500 },
      { i: 2, move: 800, pause: 500 },
      { i: 3, move: 900, pause: 500 },
      { i: 4, move: 900, pause: 800 },
    ];
    let cancelled = false;

    const doStep = (k) => {
      if (cancelled) return;
      const step = steps[k % steps.length];
      const p = points[step.i];
      const moveDur = k === 0 ? 0 : step.move;
      setCursor({ x: p.x, y: p.y, dur: moveDur });
      const t1 = setTimeout(() => {
        if (cancelled) return;
        setActiveIndex(step.i);
        const t2 = setTimeout(() => doStep(k + 1), step.pause);
        timeoutsRef.current.push(t2);
      }, moveDur + 40);
      timeoutsRef.current.push(t1);
    };

    const start = setTimeout(() => { setCursorVisible(true); doStep(0); }, 1000);
    timeoutsRef.current.push(start);

    return () => { cancelled = true; clearTimers(); };
  }, [inView, manual, points]);

  const selectRace = useCallback((idx) => {
    setManual(true);
    setCursorVisible(false);
    clearTimers();
    setActiveIndex(idx);
  }, []);

  // Mouse-wheel to cycle races (scroll up = previous, down = next)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      const dir = e.deltaY > 0 ? 1 : -1;
      const cur = activeRef.current;
      const atEnd = (dir === 1 && cur === RACES.length - 1) || (dir === -1 && cur === 0);
      if (atEnd) return; // let the page scroll past at the ends
      e.preventDefault();
      if (wheelLock.current) return;
      wheelLock.current = true;
      selectRace(Math.max(0, Math.min(RACES.length - 1, cur + dir)));
      setTimeout(() => { wheelLock.current = false; }, 260);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [selectRace]);

  const confirmRace = () => {
    toast.success(`${active.name} DEPLOYED`, { description: `Element: ${active.element} · Ready to drop, Commander.` });
  };

  const pathD = buildPath(points);

  return (
    <section
      id="races"
      data-testid="races-section"
      ref={sectionRef}
      className="relative pt-12 pb-6 px-3 sm:px-6 overflow-hidden"
    >
      {/* subtle grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 90% 80% at 50% 45%, black 0%, transparent 88%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 80% at 50% 45%, black 0%, transparent 88%)',
        }}
      />
      {/* ambient orange bloom */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 55% at 78% 62%, rgba(255,134,24,0.14) 0%, transparent 60%), radial-gradient(ellipse 50% 50% at 12% 20%, rgba(67,207,255,0.06) 0%, transparent 60%)',
        }}
      />
      {/* vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 220px rgba(0,0,0,0.85)' }} />
      {/* floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="race-particle absolute rounded-full"
            style={{
              left: p.left, top: p.top, width: p.size, height: p.size,
              background: '#FF8618', boxShadow: '0 0 8px rgba(255,134,24,0.8)',
              animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* MAIN PANEL */}
      <div
        ref={containerRef}
        className={`race-panel relative z-10 mx-auto ${inView ? 'is-in' : ''}`}
        style={{
          maxWidth: '1560px',
          padding: 'clamp(20px, 3vw, 44px)',
        }}
      >
        {/* borders removed */}

        {/* top HUD strip */}
        <div className="flex items-center justify-between mb-6" style={{ fontFamily: '"Rajdhani", sans-serif' }}>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase" style={{ color: 'var(--text-muted)' }}>
            <span className="w-2 h-2" style={{ background: '#FF8618', transform: 'rotate(45deg)', boxShadow: '0 0 8px #FF8618' }} />
            SYS.0027 // FACTION REGISTRY
          </div>
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.4em] uppercase" style={{ color: '#57E646' }}>
            <span className="w-2 h-2 rounded-full" style={{ background: '#57E646', boxShadow: '0 0 10px #57E646' }} />
            OPERATIONAL
          </div>
        </div>

        {/* Grid: left cards + right preview */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,0.86fr)_1.4fr] gap-8 lg:gap-6 items-center relative">

          {/* LEFT — angled race cards */}
          <div className="relative">
            {/* dotted cursor trail overlay */}
            {cursorVisible && points.length >= 2 && (
              <svg className="absolute inset-0 z-30" width="100%" height="100%" style={{ pointerEvents: 'none', overflow: 'visible' }}>
                <path d={pathD} className="race-trail-path" />
              </svg>
            )}
            {/* cursor + ghosts */}
            {cursorVisible && (
              <div className="absolute inset-0 z-40" style={{ pointerEvents: 'none' }} aria-hidden>
                {[3, 2, 1].map((g) => (
                  <span
                    key={g}
                    className="race-ghost-cursor"
                    style={{ transform: `translate(${cursor.x}px, ${cursor.y}px)`, transitionDuration: `${cursor.dur + g * 80}ms`, opacity: 0.08 * g + 0.04 }}
                  >
                    <CursorSvg />
                  </span>
                ))}
                <span
                  className="race-cursor"
                  data-testid="race-cursor"
                  style={{ transform: `translate(${cursor.x}px, ${cursor.y}px)`, transitionDuration: `${cursor.dur}ms` }}
                >
                  <CursorSvg />
                </span>
              </div>
            )}

            <div className="flex flex-col gap-3.5">
              {RACES.map((race, idx) => {
                const isActive = idx === activeIndex;
                const Icon = race.elementIcon;
                return (
                  <div
                    key={race.id}
                    className="race-card-wrap"
                    style={{ marginLeft: OFFSETS[idx], animationDelay: `${0.25 + idx * 0.12}s` }}
                  >
                    <button
                      ref={(el) => (cardRefs.current[idx] = el)}
                      data-testid={`race-card-${race.id}`}
                      onClick={() => selectRace(idx)}
                      onMouseEnter={() => selectRace(idx)}
                      className="race-card group relative w-full text-left focus:outline-none overflow-hidden"
                      style={{
                        height: 116,
                        maxWidth: 360,
                        clipPath: 'polygon(0% 0%, 88% 0%, 100% 100%, 8% 100%)',
                        background: isActive
                          ? `linear-gradient(135deg, rgba(21,15,8,0.96), rgba(12,14,16,0.96))`
                          : 'linear-gradient(135deg, rgba(14,19,24,0.95), rgba(22,29,36,0.9))',
                        border: isActive ? `2px solid ${race.tint}` : '1px solid rgba(255,255,255,0.10)',
                        boxShadow: isActive
                          ? `0 0 30px rgba(${race.rgb},0.55), inset 0 0 24px rgba(${race.rgb},0.10)`
                          : '0 16px 40px rgba(0,0,0,0.5)',
                        opacity: isActive ? 1 : 0.72,
                        transform: isActive ? 'scale(1.04)' : 'scale(0.985)',
                      }}
                    >
                      {/* character thumb (right) */}
                      <div
                        className="absolute right-0 top-0 bottom-0 w-[46%]"
                        style={{
                          backgroundImage: `url(${race.image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center 18%',
                          filter: isActive ? 'saturate(1.1) contrast(1.05)' : 'saturate(0.5) brightness(0.6)',
                          transition: 'filter 0.4s ease',
                          maskImage: 'linear-gradient(90deg, transparent, black 45%)',
                          WebkitMaskImage: 'linear-gradient(90deg, transparent, black 45%)',
                        }}
                      />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(6,7,8,0.96) 40%, rgba(6,7,8,0.4) 75%, transparent)' }} />

                      {/* content */}
                      <div className="relative h-full pl-10 pr-2 py-2.5 flex flex-col justify-center">
                        <div className="flex items-center gap-2">
                          <span
                            style={{
                              fontFamily: '"Teko", sans-serif', fontWeight: 700, lineHeight: 0.8,
                              fontSize: 32, color: isActive ? race.tint : '#4D555C',
                              textShadow: isActive ? `0 0 16px rgba(${race.rgb},0.7)` : 'none',
                            }}
                          >
                            {race.code}
                          </span>
                          {/* gas badge */}
                          <span
                            className="inline-flex items-center justify-center rounded-full ml-auto mr-1"
                            style={{
                              width: 34, height: 34,
                              border: `1.5px solid ${isActive ? race.tint : 'rgba(255,255,255,0.18)'}`,
                              background: 'rgba(0,0,0,0.35)',
                              boxShadow: isActive ? `0 0 14px rgba(${race.rgb},0.5)` : 'none',
                              transform: isActive ? 'scale(1.08)' : 'scale(1)',
                              transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
                              fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, fontSize: 13,
                              color: isActive ? race.tint : '#7f8992',
                            }}
                          >
                            {race.formula}
                          </span>
                        </div>
                        <h3
                          className="uppercase mt-0.5"
                          style={{
                            fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, letterSpacing: '0.06em',
                            fontSize: 21, lineHeight: 1,
                            color: isActive ? '#f2f4f6' : '#7A8188',
                            textShadow: isActive ? `0 0 12px rgba(${race.rgb},0.35)` : 'none',
                          }}
                        >
                          {race.name}
                        </h3>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Icon className="w-3 h-3" style={{ color: isActive ? race.tint : '#5A626A' }} />
                          <span className="text-[10px] uppercase tracking-[0.28em]" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 600, color: isActive ? '#A0A6AC' : '#5A626A' }}>
                            {race.element}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — title + preview */}
          <div className="race-preview-stage relative">
            {/* Title */}
            <div className="race-title-block text-center lg:text-left mb-4 lg:mb-2">
              <h2
                data-testid="races-title"
                className="uppercase leading-[0.92]"
                style={{ fontFamily: '"Teko", sans-serif', fontWeight: 700, letterSpacing: '0.01em' }}
              >
                <span style={{ display: 'block', fontSize: 'clamp(2.6rem,6vw,5.2rem)', color: 'var(--text-primary)', textShadow: '0 2px 30px rgba(0,0,0,0.6)' }}>
                  WHO WILL YOU
                </span>
                <span
                  style={{
                    display: 'block', fontSize: 'clamp(2.6rem,6vw,5.2rem)',
                    background: 'linear-gradient(180deg, #C9CDD1 0%, #9B9FA3 55%, #6F777E 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}
                >
                  DROP AS?
                </span>
              </h2>
              <p className="mt-2 text-sm" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 500, letterSpacing: '0.08em', color: 'var(--text-secondary)' }}>
                EVERY RACE. UNIQUE SURVIVAL. ONE GALAXY TO CONQUER.
              </p>
            </div>

            {/* preview area: details + character */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1.05fr] gap-4 items-center">
              {/* DETAILS */}
              <div key={`txt-${activeIndex}`} className="race-preview-text order-2 md:order-1">
                <div className="text-[13px] tracking-[0.4em] uppercase mb-1" style={{ fontFamily: '"Orbitron", sans-serif', color: 'var(--text-muted)' }}>
                  SELECTED · <span style={{ color: active.tint }} data-testid="race-preview-number">{active.code}</span> / 05
                </div>
                <h3
                  data-testid="race-preview-name"
                  className="uppercase"
                  style={{ fontFamily: '"Teko", sans-serif', fontWeight: 700, fontSize: 'clamp(2.4rem,4vw,3.4rem)', lineHeight: 0.9, color: active.tint, textShadow: `0 0 26px rgba(${active.rgb},0.45)` }}
                >
                  {active.name}
                </h3>
                {/* element badge */}
                <div className="inline-flex items-center gap-2 mt-2 px-3 py-1" style={{ border: `1px solid rgba(${active.rgb},0.55)`, background: 'rgba(0,0,0,0.35)', clipPath: 'polygon(6% 0,100% 0,94% 100%,0 100%)' }}>
                  <span style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: active.tint, fontSize: 15 }}>{active.formula}</span>
                  <span className="text-[11px] tracking-[0.24em] uppercase" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 600, color: 'var(--text-muted)' }}>{active.element}</span>
                </div>
                <p className="mt-4 max-w-md" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 500, fontSize: 17, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                  {active.desc}
                </p>

                {/* Select button */}
                <button
                  data-testid="race-select-btn"
                  onClick={confirmRace}
                  className={`race-select-btn mt-6 inline-flex items-center gap-3 px-8 ${pulsing ? 'is-pulsing' : ''}`}
                  style={{
                    height: 54,
                    background: `linear-gradient(180deg, ${active.tint} 0%, ${active.tint}CC 100%)`,
                    border: `1px solid rgba(${active.rgb},0.9)`,
                    boxShadow: `0 0 26px rgba(${active.rgb},0.5)`,
                    color: '#080808',
                    fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, fontSize: 15,
                    letterSpacing: '0.28em', textTransform: 'uppercase',
                  }}
                >
                  SELECT RACE
                  <ChevronRight className="race-btn-arrow w-5 h-5" strokeWidth={3} />
                </button>
              </div>

              {/* CHARACTER + RING */}
              <div className="order-1 md:order-2 relative flex items-center justify-center" style={{ minHeight: 430 }}>
                {/* rotating rings */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="race-ring-pulse" style={{ position: 'absolute' }}>
                    <svg className="race-ring-rotate" width="380" height="380" viewBox="0 0 380 380" style={{ maxWidth: '92%', height: 'auto', filter: `drop-shadow(0 0 24px rgba(${active.rgb},0.35))` }}>
                      <circle cx="190" cy="190" r="150" fill="none" stroke={`rgba(${active.rgb},0.55)`} strokeWidth="1.5" strokeDasharray="2 10" />
                      <circle cx="190" cy="190" r="176" fill="none" stroke={`rgba(${active.rgb},0.25)`} strokeWidth="1" strokeDasharray="40 18" />
                      {[0, 90, 180, 270].map((a) => (
                        <rect key={a} x="188" y="8" width="4" height="18" fill={active.tint} transform={`rotate(${a} 190 190)`} />
                      ))}
                    </svg>
                  </div>
                  <svg className="race-ring-rotate-rev" width="300" height="300" viewBox="0 0 300 300" style={{ position: 'absolute', maxWidth: '74%', height: 'auto' }}>
                    <circle cx="150" cy="150" r="128" fill="none" stroke={`rgba(${active.rgb},0.18)`} strokeWidth="1" strokeDasharray="6 14" />
                  </svg>
                  {/* flash on change */}
                  <div
                    key={`flash-${activeIndex}`}
                    className="race-ring-flash absolute rounded-full"
                    style={{ width: 340, height: 340, maxWidth: '86%', border: `2px solid ${active.tint}`, boxShadow: `0 0 40px rgba(${active.rgb},0.6)` }}
                  />
                </div>

                {/* character */}
                <img
                  key={`char-${activeIndex}`}
                  src={active.image}
                  alt={active.name}
                  className="race-preview-char relative z-10"
                  style={{
                    height: 430, maxHeight: '48vw', width: 'auto', objectFit: 'contain',
                    filter: `drop-shadow(0 24px 50px rgba(${active.rgb},0.35)) drop-shadow(0 10px 20px rgba(0,0,0,0.7))`,
                    maskImage: 'linear-gradient(to bottom, #000 0%, #000 78%, rgba(0,0,0,0.4) 92%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 78%, rgba(0,0,0,0.4) 92%, transparent 100%)',
                  }}
                />

                {/* base glow */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-none" style={{ width: '70%', height: 40, background: `radial-gradient(ellipse at center, rgba(${active.rgb},0.5) 0%, transparent 70%)`, filter: 'blur(6px)' }} />

                {/* READY TO DROP tag */}
                <div
                  className="absolute top-2 right-2 sm:top-4 sm:right-5 z-20 whitespace-nowrap pointer-events-none text-[10px] tracking-[0.35em] uppercase"
                  style={{ fontFamily: '"Orbitron", sans-serif', color: active.tint }}
                >
                  READY TO DROP
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Races;
